/** Text as the app folds it (TrainingPlanText, ExerciseKey), and which rows are an athlete's (PublishedWeeks.mine). */
import { mondayOf } from "./days.js";
import type { PlanRow } from "./types.js";

function stripMarks(s: string): string {
  return String(s).normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC");
}
const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;

export function fold(text: string): string {
  const folded = stripMarks(text).toLowerCase();
  let out = "";
  let lastWasSpace = true;
  for (const ch of folded) {
    if (LETTER_OR_NUMBER.test(ch)) {
      out += ch;
      lastWasSpace = false;
    } else if (!lastWasSpace) {
      out += " ";
      lastWasSpace = true;
    }
  }
  return out.trim();
}

function words(text: string): string[] {
  return fold(text).split(" ").filter(Boolean);
}

const EVERYONE = new Set(["tous", "tout", "toutes", "all", "everyone", "everybody", "group", "groupe"]);
export function isEveryone(group: string): boolean {
  return EVERYONE.has(fold(group));
}

function containsRun(haystack: string[], needle: string[]): boolean {
  if (needle.length > haystack.length) return false;
  for (let start = 0; start <= haystack.length - needle.length; start++) {
    if (needle.every((w, i) => haystack[start + i] === w)) return true;
  }
  return false;
}

export function matchesGroup(group: string, name: string): boolean {
  const g = words(group);
  const n = words(name);
  if (!g.length || !n.length) return false;
  if (g.join(" ") === n.join(" ")) return true;
  if (containsRun(g, n) || containsRun(n, g)) return true;
  const gt = g.join("");
  const nt = n.join("");
  if (Math.min([...gt].length, [...nt].length) < 3) return false;
  return gt.includes(nt) || nt.includes(gt);
}

export function columnFor(columns: string[], myGroups: string[]): string | null {
  const personal = columns.filter((c) => !isEveryone(c));
  for (const mine of myGroups) {
    for (const column of personal) if (matchesGroup(column, mine)) return column;
  }
  return null;
}

export function weekColumns(sheet: string[] | null | undefined, weekRows: PlanRow[]): string[] {
  const columns = (sheet ?? []).filter((c) => !isEveryone(c));
  for (const row of weekRows) if (row.group && !columns.includes(row.group)) columns.push(row.group);
  return columns;
}

export function mine(
  rows: PlanRow[],
  sheets: { [week: string]: string[] | undefined },
  myGroups: string[] | null | undefined,
): { sessions: PlanRow[]; unmatchedWeeks: { [week: string]: string[] } } {
  const byWeek = new Map<string, PlanRow[]>();
  for (const row of rows) {
    const week = row.week || mondayOf(row.date);
    const list = byWeek.get(week);
    if (list) list.push(row);
    else byWeek.set(week, [row]);
  }
  const sessions: PlanRow[] = [];
  const unmatchedWeeks: { [week: string]: string[] } = {};
  for (const week of [...byWeek.keys()].sort()) {
    const weekRows = byWeek.get(week)!;
    const columns = weekColumns(sheets[week], weekRows);
    const chosen = columnFor(columns, myGroups ?? []) ?? "";
    if (!chosen && columns.length) unmatchedWeeks[week] = columns;
    for (const row of weekRows) if (row.group === chosen) sessions.push(row);
  }
  return { sessions, unmatchedWeeks };
}

// MARK: Exercise keys (ExerciseKey).

const EXPANSIONS = new Map([
  ["db", "dumbbell"], ["bb", "barbell"], ["kb", "kettlebell"], ["dl", "deadlift"], ["tbdl", "trap bar deadlift"],
  ["sldl", "single leg deadlift"], ["rdl", "romanian deadlift"], ["ohp", "overhead press"], ["sl", "single leg"],
  ["bw", "bodyweight"], ["ghr", "glute ham raise"], ["cgbp", "close grip bench press"],
  ["dumbell", "dumbbell"], ["dumbel", "dumbbell"], ["kettelbell", "kettlebell"],
  ["pullup", "pull up"], ["pushup", "push up"], ["chinup", "chin up"], ["situp", "sit up"], ["stepup", "step up"],
]);
const JOINERS = new Set(["from", "to", "into", "onto", "with", "on", "in", "at", "off", "per", "behind", "under", "over", "across", "around", "against", "and"]);
const INVARIANT = new Set(["press", "triceps", "biceps", "abs", "lats", "delts", "pecs", "quads"]);
const SEPARATORS = new Set(["-", "_", "/", "–", "—"]);

function singular(word: string): string {
  if (INVARIANT.has(word) || /\p{N}/u.test(word)) return word;
  const length = [...word].length;
  if (!word.endsWith("s") || length <= 2) return word;
  if (word.endsWith("ies") && length > 4) return word.slice(0, -3) + "y";
  for (const ending of ["sses", "ches", "shes", "xes", "zes"]) if (word.endsWith(ending)) return word.slice(0, -2);
  if (word.endsWith("ss")) return word;
  return word.slice(0, -1);
}

function expansion(word: string): string | null {
  const direct = EXPANSIONS.get(word);
  if (direct) return direct;
  if (!word.endsWith("s")) return null;
  return EXPANSIONS.get(singular(word)) ?? null;
}

export function exerciseKey(name: string): string {
  const folded = stripMarks(name).toLowerCase();
  let scalars = "";
  for (const ch of folded) {
    if (LETTER_OR_NUMBER.test(ch)) scalars += ch;
    else if (/\s/u.test(ch) || SEPARATORS.has(ch)) scalars += " ";
  }
  const list: string[] = [];
  for (const w of scalars.split(" ").filter(Boolean)) {
    const e = expansion(w);
    if (e) list.push(...e.split(" "));
    else list.push(w);
  }
  if (!list.length) return "";
  for (let i = 0; i < list.length - 1; i++) if (JOINERS.has(list[i + 1]!)) list[i] = singular(list[i]!);
  list[list.length - 1] = singular(list[list.length - 1]!);
  return list.join(" ");
}
